/**
 * 曲库分区：首页「教材」只留两类内容，其余全部进公共区。
 *
 * ## 为什么要有这个文件
 *
 * `library_items` 里那 500 多首是按**音乐体裁**分类的（练习曲 / 复调 / 小奏鸣曲…），
 * 那是"给想找曲子的人"用的检索维度。而首页要回答的是另一个问题：
 * 「哪些是我们担保的教学材料」。两者不是一回事 —— 库里 137 首练习曲里，
 * 我们只把拜厄 Op.101 的 5 首和车尔尼 Op.599 的 14 首算作教材，
 * 其余 Czerny Op.139/299/821 之类留在公共区，跟体裁分类并不冲突。
 *
 * 所以这里**不改库里的 category**（管理端、导入器、接口都依赖它），
 * 而是在前端按「作曲家 + 作品号」再切一刀。
 *
 * ## 为什么按标题的作品号认，不写死 id
 *
 * 曲库 id 是「分类 + 分类内序号」（`etude-0001`），重新枚举源目录就会整体位移
 * —— 2026-10-26 实测 391 条里 357 条换了作品。写死 id 那种做法，
 * 曲库一重建就会静默变成"教材里放的是别的曲子"，且报错全无。
 * 作曲家 + 作品号是谱面自身的属性，重建前后不变。
 *
 * ## 判据（改这里之前先数一遍）
 *
 * · 拜厄：composer 含 Beyer 且标题含 `Op.101` → 5 首（etude-0001…0005）
 * · 车尔尼：composer 含 Czerny 且标题含 `Op.599` → 14 首（etude-0102…0115）
 * 库里还有 32 首其他车尔尼（Op.139/299/821/740…）**不进教材**，见「公共区」。
 */
import type { LibraryItem } from './library'

export type PracticeCollection = 'beyer' | 'czerny'

export type PracticeSpec = {
	id: PracticeCollection
	/** chip 上的名字 */
	name: string
	/**
	 * 作品号。**必须跟名字一起显示**：车尔尼在库里还有 46 首，
	 * 只写「车尔尼 14」会让人以为漏了 32 首。
	 */
	opus: string
	/** 选中后显示的一句说明 */
	desc: string
	composerMatch: RegExp
	titleMatch: RegExp
}

export const PRACTICE_COLLECTIONS: PracticeSpec[] = [
	{
		id: 'beyer',
		name: '拜厄',
		opus: 'Op.101',
		desc: '拜厄《钢琴基本教程》Op.101 选段：识谱、五指位置与双手交替，入门第一年就弹得到',
		composerMatch: /beyer/i,
		titleMatch: /op\.?\s*101\b/i,
	},
	{
		id: 'czerny',
		name: '车尔尼',
		opus: 'Op.599',
		desc: '车尔尼《钢琴初级教程》Op.599 选段：从固定手位走到简单音阶的渐进练习',
		composerMatch: /czerny/i,
		titleMatch: /op\.?\s*599\b/i,
	},
]

/**
 * 这首属于哪本教材？不是教材返回 null（= 公共区）。
 *
 * 作曲家与标题拼起来一起试：源数据里作曲家字段有时写成 `C. Czerny`、
 * 有时标题里又带一遍 `Carl Czerny Op. 599 No. 1`，两边都可能命中。
 */
export function practiceCollectionOf(item: LibraryItem): PracticeCollection | null {
	const probe = `${item.composer} ${item.title}`
	for (const spec of PRACTICE_COLLECTIONS) {
		if (spec.composerMatch.test(probe) && spec.titleMatch.test(probe)) return spec.id
	}
	return null
}

/** 标题里的第一个练习编号，用来把教材条目按难度进程排（`Op.599 No.10` → 10） */
function practiceOrder(item: LibraryItem): number {
	const m = /no\.?\s*(\d+)/i.exec(item.title)
	return m ? Number(m[1]) : 9999
}

export type LibrarySections = {
	/** 编谱示例（库里 `category='demo'` 的那批，静态样张另算） */
	demo: LibraryItem[]
	/** 教材练习曲，按教程分开 */
	practice: Record<PracticeCollection, LibraryItem[]>
	/** 教材练习曲合计（首页标题那个数） */
	practiceAll: LibraryItem[]
	/** 公共区：除教材之外的全部曲目，仍带着原来的体裁与难度 */
	community: LibraryItem[]
}

/**
 * 把整份目录切成「教材」与「公共区」。
 *
 * 教材那两本各自按练习编号排 —— 目录里的顺序是字母序
 * （`No.1` / `No.10` / `No.19` / `No.2`），照着放会让人以为第 4 首才是第 2 条练习。
 */
export function splitLibrary(items: LibraryItem[]): LibrarySections {
	const out: LibrarySections = {
		demo: [],
		practice: {beyer: [], czerny: []},
		practiceAll: [],
		community: [],
	}
	for (const item of items) {
		if (item.category == 'demo') {
			out.demo.push(item)
			continue
		}
		const coll = practiceCollectionOf(item)
		if (coll) {
			out.practice[coll].push(item)
			out.practiceAll.push(item)
			continue
		}
		out.community.push(item)
	}
	for (const coll of Object.keys(out.practice) as PracticeCollection[]) {
		out.practice[coll].sort((a, b) => practiceOrder(a) - practiceOrder(b))
	}
	out.practiceAll.sort((a, b) => practiceOrder(a) - practiceOrder(b))
	return out
}
